import { useState, useEffect } from 'react';

import {
  Car,
  ChevronDown,
  ChevronRight,
  CreditCard,
  History,
  Home,
  LayoutDashboard,
  Users,
  Settings,
  Megaphone,
  Plus,
  Phone,
  TrendingUp,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  Bot,
  Database,
  LogOut,
  CircleUser,
  PanelLeftClose,
  PanelLeft,
  Sun,
  Moon,
  DollarSign,
  Layers,
  ListChecks,
  PhoneCall,
  BookUser,
  MessageSquare,
  ShieldCheck,
  Sliders,
  FileText,
  FileSignature,
  FlaskConical,
  GitBranch,
  GitCompareArrows,
  Grid3x3,
  Webhook,
  Repeat,
  ClipboardX,
  Code2,
  CodeXml,
  Wrench,
  BrainCircuit,
  Boxes,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSignaturesPendingCount } from '@/hooks/useSignatures';
import { getEnv } from '@/lib/env';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useBrandingStore } from '@/stores/branding.store';
import { useFeaturesStore } from '@/stores/features.store';

import type { LucideIcon } from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon?: LucideIcon;
  // Advanced/technical item hidden from the default "light" client view.
  devOnly?: boolean;
}

interface NavGroup {
  title: string;
  icon: LucideIcon;
  items: NavItem[];
  condition?: boolean;
  forceCollapsible?: boolean;
  // Advanced/technical resources hidden from the default "light" client view.
  // Only shown when the user enables Dev view from the sidebar toggle.
  devOnly?: boolean;
  // Red attention badge (e.g. documents awaiting signature). Hidden when 0.
  badge?: number;
}

const DEV_VIEW_STORAGE_KEY = 'devView';

interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export const Sidebar = ({ collapsed = false, onCollapsedChange }: SidebarProps) => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const { companyName, logoUrl, logoUrlDark } = useBrandingStore();
  const {
    chatbot: featureChatbot,
    gcpCosts: featureGcpCosts,
    twilioCosts: featureTwilioCosts,
    smsActionsModule: featureSmsActionsModule,
    reports: featureReports,
    webhooks: featureWebhooks,
    paymentLinks: featurePaymentLinks,
    digitalSign: featureDigitalSign,
    dealerKit: featureDealerKit,
    stockSync: featureStockSync,
    bookings: featureBookings,
    financeApplications: featureFinanceApplications,
    agentImprovement: featureAgentImprovement,
    compareKpis: featureCompareKpis,
    analyticsReporter: featureAnalyticsReporter,
  } = useFeaturesStore();

  // Clients view: 'client' role users only get the Calls navigation.
  const isClientView = user?.role === 'client';

  // Red alert badge: documents still awaiting a signature.
  const { data: pendingSignaturesData } = useSignaturesPendingCount(
    featureDigitalSign && !isClientView,
  );
  const pendingSignatures = pendingSignaturesData?.count ?? 0;

  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  // Sync with external prop (e.g. from sidebar store)
  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);
  // Dev view exposes advanced/technical resources. Defaults to off (light client view),
  // persisted to localStorage so the choice survives refresh.
  const [isDevView, setIsDevView] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(DEV_VIEW_STORAGE_KEY) === 'true';
    }
    return false;
  });
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored) return stored === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleCollapsed = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    onCollapsedChange?.(newValue);
  };

  const toggleDarkMode = () => setIsDark((prev) => !prev);

  const toggleDevView = () =>
    setIsDevView((prev) => {
      const next = !prev;
      localStorage.setItem(DEV_VIEW_STORAGE_KEY, String(next));
      return next;
    });

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Only exact match - don't highlight parent routes for detail pages
    return location.pathname === path;
  };

  const isGroupActive = (items: NavItem[]) => {
    return items.some((item) => isActive(item.path));
  };

  // Define navigation groups
  const navGroups: NavGroup[] = [
    {
      title: 'Home',
      icon: Home,
      items: [{ label: 'Home', path: '/' }],
    },
    {
      title: 'Analytics',
      icon: LayoutDashboard,
      items: [{ label: 'Analytics', path: '/analytics' }],
    },
    {
      title: 'Assistant',
      icon: Bot,
      items: [
        { label: 'Chats', path: '/chatbot' },
        { label: 'Tools', path: '/chatbot/tools', devOnly: true },
      ],
      condition: featureChatbot,
    },
    {
      title: 'Call Quality',
      icon: ShieldCheck,
      items: [{ label: 'Call Quality', path: '/call-quality' }],
    },
    {
      title: 'Agent Improvement',
      icon: Repeat,
      items: [
        { label: 'Overview', path: '/agent-improvement', icon: LayoutDashboard },
        { label: 'Pipeline Runs', path: '/agent-improvement/runs', icon: GitBranch },
        { label: 'Replays', path: '/agent-improvement/replays', icon: FlaskConical },
        { label: 'Scenarios', path: '/agent-improvement/scenarios', icon: ListChecks },
        { label: 'Scenario Runs', path: '/agent-improvement/scenario-runs', icon: Grid3x3 },
        { label: 'Misclassified Calls', path: '/misclassified-calls', icon: ClipboardX },
        { label: 'Judge Tuning', path: '/agent-improvement/judge-tuning', icon: Repeat },
      ],
      condition: featureAgentImprovement,
      devOnly: true,
    },
    {
      title: 'Compare KPIs',
      icon: GitCompareArrows,
      items: [{ label: 'Compare KPIs', path: '/compare-kpis' }],
      condition: featureCompareKpis,
    },
    {
      title: 'Users',
      icon: Users,
      items: [{ label: 'Users', path: '/users' }],
      devOnly: true,
    },
    {
      title: 'Activity Log',
      icon: History,
      items: [{ label: 'All Activity', path: '/activity-log' }],
      // The trail exposes who changed what across every module, plus the
      // submitted values — admins only, matching the API.
      condition: user?.role === 'admin',
    },
    {
      title: 'Configurations',
      icon: Settings,
      items: [
        { label: 'All Configurations', path: '/configurations' },
        { label: 'Agent Definitions', path: '/agent-definitions' },
      ],
      devOnly: true,
    },
    {
      title: 'Custom Tools',
      icon: Wrench,
      items: [
        { label: 'Custom Tools', path: '/custom-tools' },
        { label: 'Scrapers', path: '/scrapers' },
      ],
      devOnly: true,
      forceCollapsible: true,
    },
    {
      title: 'Campaigns',
      icon: Megaphone,
      items: [
        { label: 'All Campaigns', path: '/campaigns' },
        { label: 'Add Campaign', path: '/campaigns/new', icon: Plus },
      ],
    },
    {
      title: 'Sales Console',
      icon: BookUser,
      items: [{ label: 'Sales Console', path: '/sales-console' }],
    },
    {
      title: 'Calls',
      icon: Phone,
      items: [{ label: 'All Calls', path: '/calls' }],
    },
    {
      title: 'Phone Numbers',
      icon: PhoneCall,
      items: [
        { label: 'Twilio', path: '/twilio-numbers' },
        { label: 'Telnyx', path: '/telnyx-numbers' },
        { label: 'WhatsApp', path: '/whatsapp-numbers' },
        { label: 'WA Templates', path: '/whatsapp-templates' },
      ],
      devOnly: true,
    },
    {
      title: 'Reports',
      icon: TrendingUp,
      items: [{ label: 'View Reports', path: '/reports' }],
      condition: featureReports,
    },
    {
      title: 'AI Analyst',
      icon: Bot,
      items: [{ label: 'Reports', path: '/ai-analyst' }],
      condition: featureAnalyticsReporter,
    },
    {
      title: 'Availability Dashboard',
      icon: LayoutDashboard,
      items: [{ label: 'Availability Dashboard', path: '/availability-dashboard' }],
      condition: getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS',
    },
    {
      title: 'Availability Leads',
      icon: CalendarCheck,
      items: [{ label: 'Availability Leads', path: '/availability-leads' }],
      condition: getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS',
    },
    {
      title: 'Shift Confirmations',
      icon: CalendarCheck,
      items: [{ label: 'Shift Confirmations', path: '/shift-confirmations' }],
      condition: getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'ROYALTY_STAFFING',
    },
    {
      title: 'Reviews',
      icon: ClipboardCheck,
      items: [
        { label: 'Campaign Reviews', path: '/reviews/campaigns', icon: Layers },
        { label: 'Call Reviews', path: '/reviews/calls', icon: PhoneCall },
        { label: 'Review Configs', path: '/reviews/configs', icon: Settings },
      ],
      devOnly: true,
    },
    {
      title: 'Workflows',
      icon: GitBranch,
      items: [
        { label: 'All Workflows', path: '/workflows' },
        { label: 'Execution History', path: '/workflows/history', icon: History },
      ],
      devOnly: true,
    },
    {
      title: 'Workflow Templates',
      icon: FileText,
      items: [{ label: 'Templates', path: '/workflow-templates' }],
      devOnly: true,
    },
    {
      title: 'Bookings',
      icon: CalendarDays,
      items: [{ label: 'Bookings', path: '/bookings' }],
      condition: featureBookings,
    },
    {
      title: 'Scheduled Sends',
      icon: CalendarClock,
      items: [{ label: 'Scheduled Sends', path: '/scheduled-sends' }],
    },
    {
      title: 'Finance Applications',
      icon: FileSignature,
      items: [{ label: 'Applications', path: '/finance-applications' }],
      condition: featureFinanceApplications,
    },
    {
      title: 'SMS Actions',
      icon: MessageSquare,
      items: [{ label: 'Configurations', path: '/sms-actions' }],
      condition: featureSmsActionsModule,
      devOnly: true,
    },
    {
      title: 'Payments',
      icon: CreditCard,
      items: [{ label: 'Payments', path: '/payments' }],
      condition: featurePaymentLinks,
    },
    {
      title: 'Signatures',
      icon: FileSignature,
      items: [{ label: 'Digital Signatures', path: '/signatures' }],
      condition: featureDigitalSign,
      badge: pendingSignatures,
    },
    {
      title: 'Webhooks',
      icon: Webhook,
      items: [{ label: 'Webhooks', path: '/webhooks' }],
      condition: featureWebhooks,
      devOnly: true,
    },
    {
      title: 'CRM Integration',
      icon: Car,
      items: [
        { label: 'Registrations', path: '/crm-integration' },
        { label: 'Contacts', path: '/crm-integration/contacts' },
        { label: 'Stock', path: '/crm-integration/stock' },
      ],
      condition: featureDealerKit,
      devOnly: true,
    },
    {
      title: 'Stock Sync',
      icon: Boxes,
      items: [{ label: 'Stock Sources', path: '/stock-sync' }],
      condition: featureStockSync,
      devOnly: true,
    },
    {
      title: 'Costs',
      icon: DollarSign,
      items: [
        ...(featureGcpCosts ? [{ label: 'GCP Costs', path: '/platform-costs' }] : []),
        ...(featureTwilioCosts ? [{ label: 'Twilio Costs', path: '/twilio-costs' }] : []),
      ],
      condition: featureGcpCosts || featureTwilioCosts,
      forceCollapsible: true,
      devOnly: true,
    },
    {
      title: 'Redis',
      icon: Database,
      items: [{ label: 'Redis Dashboard', path: '/redis' }],
      // Dev-only tool — hidden in production builds
      condition: import.meta.env.DEV,
      devOnly: true,
    },
    {
      title: 'AI Agents',
      icon: BrainCircuit,
      items: [
        { label: 'Classifier Configs', path: '/classifier-configs' },
        { label: 'Quality Configs', path: '/quality-configs' },
        { label: 'Classifier Executions', path: '/classifier-executions' },
        { label: 'Quality Executions', path: '/quality-executions' },
      ],
      devOnly: true,
    },
    {
      title: 'Platform Settings',
      icon: Sliders,
      items: [
        { label: 'Platform Settings', path: '/settings' },
        { label: 'Dashboard Configs', path: '/dashboard-configs' },
        { label: 'Metric Definitions', path: '/metric-definitions' },
        { label: 'Call Outcomes', path: '/outcomes' },
      ],
      // Settings is always available on every platform (no feature gate).
      devOnly: true,
    },
  ];

  // Track which groups are open
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const initialOpen = new Set<string>();
    navGroups.forEach((group) => {
      if (isGroupActive(group.items)) {
        initialOpen.add(group.title);
      }
    });
    return initialOpen;
  });

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  return (
    <aside
      className={cn(
        'bg-card flex h-screen flex-col border-r transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo/Brand */}
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!isCollapsed && (
          <Link to="/" className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={isDark && logoUrlDark ? logoUrlDark : logoUrl}
                alt={companyName}
                className="h-8"
              />
            ) : (
              <span className="text-foreground text-xl font-bold">{companyName}</span>
            )}
          </Link>
        )}
        <div className="flex items-center gap-1">
          {!isCollapsed && !isClientView && (
            <button
              onClick={toggleDevView}
              title={
                isDevView
                  ? 'Dev view ON — click to hide advanced resources'
                  : 'Dev view OFF — click to show advanced resources'
              }
              aria-pressed={isDevView}
              className={cn(
                'rounded-lg p-2 transition-colors',
                isDevView
                  ? 'bg-primary text-primary-foreground dark:text-white'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {isDevView ? <Code2 className="h-5 w-5" /> : <CodeXml className="h-5 w-5" />}
            </button>
          )}
          <button
            onClick={toggleCollapsed}
            className="text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-lg p-2 transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <PanelLeft className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {navGroups.map((group) => {
            // Clients view: only the Calls group is available.
            if (isClientView && group.title !== 'Calls') return null;
            if (group.condition === false) return null;
            // Hide advanced/technical groups unless Dev view is enabled.
            if (group.devOnly && !isDevView) return null;

            // Hide advanced/technical items within a group unless Dev view is enabled.
            const visibleItems = group.items.filter((item) => !item.devOnly || isDevView);
            if (visibleItems.length === 0) return null;

            const isOpen = openGroups.has(group.title);
            const groupActive = isGroupActive(visibleItems);
            const Icon = group.icon;

            // Single item group - render as direct link (unless forceCollapsible)
            if (visibleItems.length === 1 && !group.forceCollapsible) {
              const item = visibleItems[0];
              return (
                <Link
                  key={group.title}
                  to={item.path}
                  title={isCollapsed ? group.title : undefined}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground dark:text-white'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:text-white',
                    isCollapsed && 'justify-center px-2',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!isCollapsed && (
                    <span className="flex flex-1 items-center justify-between">
                      {group.title}
                      {!!group.badge && (
                        <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                          {group.badge}
                        </span>
                      )}
                    </span>
                  )}
                </Link>
              );
            }

            // Collapsed mode - show only icons with first item link
            if (isCollapsed) {
              return (
                <Link
                  key={group.title}
                  to={visibleItems[0].path}
                  title={group.title}
                  className={cn(
                    'flex items-center justify-center rounded-lg px-2 py-2 text-sm font-medium transition-colors',
                    groupActive
                      ? 'bg-primary text-primary-foreground dark:text-white'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:text-white',
                  )}
                >
                  <span className="relative">
                    <Icon className="h-4 w-4 shrink-0" />
                    {!!group.badge && (
                      <span className="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-red-600" />
                    )}
                  </span>
                </Link>
              );
            }

            // Multi-item group - render as collapsible
            return (
              <Collapsible
                key={group.title}
                open={isOpen}
                onOpenChange={() => toggleGroup(group.title)}
              >
                <CollapsibleTrigger
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    groupActive
                      ? 'bg-accent text-accent-foreground dark:text-white'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:text-white',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 shrink-0" />
                    {group.title}
                  </div>
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1 space-y-1 pl-4">
                  {visibleItems.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                          isActive(item.path)
                            ? 'bg-primary text-primary-foreground dark:text-white'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:text-white',
                        )}
                      >
                        {ItemIcon && <ItemIcon className="h-3 w-3" />}
                        {item.label}
                      </Link>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="border-t p-2">
        {user && (
          <div
            className={cn(
              'mb-2 flex items-center gap-3 rounded-lg px-3 py-2',
              isCollapsed && 'justify-center px-2',
            )}
          >
            <CircleUser className="text-muted-foreground h-5 w-5 shrink-0" />
            {!isCollapsed && (
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-foreground truncate font-medium">{user.name}</p>
                <p className="text-muted-foreground truncate text-xs">{user.role}</p>
              </div>
            )}
            <button
              onClick={toggleDarkMode}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-lg p-1.5 transition-colors"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        )}
        <button
          onClick={logout}
          title={isCollapsed ? 'Log out' : undefined}
          className={cn(
            'text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isCollapsed && 'justify-center px-2',
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!isCollapsed && 'Log out'}
        </button>
      </div>
    </aside>
  );
};
